import type { PaymentSubject } from './api/memberPayment';

// ═══════════════════════════════════════════════════════════════════
// COMING BACK FROM VIPPS (#508).
//
// Vipps is a REDIRECT method: `confirmPayment` sends the member away — to the
// Vipps app on a phone, to a Vipps page with a phone push on a desktop, to
// Stripe's approval page in test mode — and Stripe sends them back to a
// `return_url` with four parameters appended: `payment_intent`,
// `payment_intent_client_secret`, `redirect_pm_type` and `redirect_status`.
// The page LOADS AGAIN from scratch. This module is the two ends of that trip.
//
// ── THE RETURN URL CARRIES NO FRAGMENT, ON PURPOSE ──────────────────
//
// The player app lives at `/#mobile`, and the obvious return URL is
// `window.location.href` — which ends in that fragment. Where Stripe's own
// redirect appends its parameters to a URL with a `#` in it is not something we
// can verify from here, and the failure is total: parameters after the `#` are
// not a query at all, and a `#mobile` swallowed into the query string drops the
// member on the club PORTAL's surface instead of their own (`useNavigation`'s
// `isMemberSurfaceLocation` reads the hash). So the URL handed to Stripe has no
// fragment, and the view travels as a query parameter of our own (`sp_view`),
// which Stripe documents as preserved: «you can also append your own query
// parameters when providing the return_url, which persist through the redirect».
//
// ── THE CLUB TRAVELS TOO ─────────────────────────────────────────────
//
// The SESSION survives the trip: a player's `sps_` session is an HttpOnly
// cookie with a `Max-Age` (#569, #750, #1091), and `restore()` finds it on the
// next load. The ENTERED CLUB does not — `selectPlayerClub` is in memory by
// design (#800), so a reload lands on the player's own Hjem with no club, and
// every `/member/*` request would go out naming none. The club they were paying
// in is carried as `sp_club` and re-entered ONCE, by the account that restores
// (`Padel24App`). It grants nothing: the server checks the roster row for the
// club a request names, on every request.
//
// ── AND ALL OF IT IS STRIPPED BEFORE ANYTHING ELSE READS THE URL ─────
//
// `capturePaymentReturn` runs first thing on boot (`paymentReturnBoot.ts`). It
// takes the parameters into memory and rewrites the address without them, so:
//   • a reload does not announce the payment a second time;
//   • `payment_intent_client_secret` — which can confirm the intent — does not
//     sit in the address bar, the history, or a shared link;
//   • the hash is back before `useNavigation` and the surface gate look for it.
// ═══════════════════════════════════════════════════════════════════

/** Our own return parameters. Prefixed so nothing else in the app collides with them. */
const OURS = ['sp_pay', 'sp_view', 'sp_club'] as const;

/** What Stripe appends. All of it goes — the client secret most of all. */
const STRIPES = [
  'payment_intent',
  'payment_intent_client_secret',
  'redirect_pm_type',
  'redirect_status',
] as const;

/** The club a player had entered, as `customerContext` holds it. */
export interface ReturnClub {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
}

/** What came back, kept in memory for the screen that announces it. */
export interface PaymentReturn {
  readonly subject: PaymentSubject;
  readonly bookingId: string;
  /**
   * Stripe's `redirect_status`: `succeeded`, `processing`, `failed`, … — what
   * the BROWSER was told. Never the authority on money: the screen asks the
   * server, and this only decides what to say while the webhook is on its way.
   */
  readonly redirectStatus: string;
  readonly club: ReturnClub | null;
}

/** A view name is a word, nothing else — it is written back into the address. */
const VIEW = /^[A-Za-z0-9_-]{1,40}$/;

/**
 * THE URL TO HAND `confirmPayment` (#508). No fragment — see the header.
 *
 * Built from the page's own location, so the member comes back to the same
 * origin, path and query they left from; the hash becomes `sp_view`.
 */
export function buildPaymentReturnUrl(
  location: Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'>,
  payment: { subject: PaymentSubject; bookingId: string; club: ReturnClub | null },
): string {
  const params = withoutReturnParams(location.search);
  params.set('sp_pay', `${payment.subject}:${payment.bookingId}`);
  const view = location.hash.replace(/^#/, '');
  if (VIEW.test(view)) params.set('sp_view', view);
  if (payment.club !== null) {
    params.set(
      'sp_club',
      JSON.stringify({ id: payment.club.id, slug: payment.club.slug, name: payment.club.name }),
    );
  }
  return `${location.origin}${location.pathname}?${params.toString()}`;
}

let captured: PaymentReturn | null = null;

/**
 * TAKE THE RETURN OUT OF THE ADDRESS, ONCE, ON BOOT.
 *
 * Does nothing at all on a URL that carries neither our marker nor Stripe's
 * parameters. Otherwise it ALWAYS rewrites the address — even when what came
 * back cannot be read — because a client secret left in the address bar is the
 * worse outcome of the two.
 */
export function capturePaymentReturn(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>,
  history: Pick<History, 'replaceState' | 'state'>,
): PaymentReturn | null {
  const params = new URLSearchParams(location.search);
  const touched = [...OURS, ...STRIPES].some((k) => params.has(k));
  if (!touched) return null;

  const view = params.get('sp_view');
  const rest = withoutReturnParams(location.search).toString();
  const hash = view !== null && VIEW.test(view) ? `#${view}` : location.hash;
  history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${hash}`);

  const redirectStatus = params.get('redirect_status');
  const pay = parsePay(params.get('sp_pay'));
  // NOTHING TO ANNOUNCE without both halves: our marker says WHICH booking,
  // Stripe's status says that a redirect happened at all.
  captured =
    pay === null || redirectStatus === null || redirectStatus === ''
      ? null
      : { ...pay, redirectStatus, club: parseClub(params.get('sp_club')) };
  return captured;
}

/** The captured return, still there — for the club re-entry, which does not consume it. */
export function peekPaymentReturn(): PaymentReturn | null {
  return captured;
}

/** The captured return, handed over ONCE — for the screen that announces it. */
export function takePaymentReturn(): PaymentReturn | null {
  const taken = captured;
  captured = null;
  return taken;
}

/** For tests only: forget anything captured. */
export function resetPaymentReturnForTests(): void {
  captured = null;
}

function withoutReturnParams(search: string): URLSearchParams {
  const params = new URLSearchParams(search);
  for (const k of [...OURS, ...STRIPES]) params.delete(k);
  return params;
}

function parsePay(value: string | null): { subject: PaymentSubject; bookingId: string } | null {
  if (value === null) return null;
  const at = value.indexOf(':');
  if (at < 0) return null;
  const subject = value.slice(0, at);
  const bookingId = value.slice(at + 1);
  if ((subject !== 'booking' && subject !== 'equipment') || bookingId === '') return null;
  return { subject, bookingId };
}

function parseClub(value: string | null): ReturnClub | null {
  if (value === null) return null;
  try {
    const raw = JSON.parse(value) as Record<string, unknown> | null;
    if (
      raw !== null &&
      typeof raw === 'object' &&
      typeof raw.id === 'string' &&
      raw.id !== '' &&
      typeof raw.slug === 'string' &&
      typeof raw.name === 'string'
    ) {
      return { id: raw.id, slug: raw.slug, name: raw.name };
    }
  } catch {
    // An unreadable club is no club: the player lands on their own Hjem.
  }
  return null;
}
